// src/pages/DashboardProject/ContractorCard.tsx
import React from 'react';
import { Card, CardBody, Badge } from 'reactstrap';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Star, ExternalLink } from 'lucide-react';
import { Contractor } from '../../models/Contractor.interface';
import { formatAddress } from '../../models/Address.interface';

interface ContractorCardProps {
  contractor: Contractor;
}

const ContractorCard: React.FC<ContractorCardProps> = ({ contractor }) => {
  // Take first letter of business name for avatar
  const avatarLetter = contractor.businessName ? 
    contractor.businessName.charAt(0).toUpperCase() : '?';
  
  // Format rating display
  const renderRating = () => {
    if (!contractor.rating && !contractor.reviewCount) return null;
    
    return (
      <div className="d-flex align-items-center text-warning">
        <Star size={16} fill="currentColor" className="me-1" />
        <span>
          {contractor.rating?.toFixed(1) || '0.0'}
          {contractor.reviewCount && (
            <small className="text-muted ms-1">({contractor.reviewCount})</small>
          )}
        </span>
      </div>
    );
  };

  // Get a subset of categories to display
  const displayCategories = contractor.categories?.slice(0, 3) || [];
  const additionalCategories = contractor.categories && 
    contractor.categories.length > 3 ? 
    contractor.categories.length - 3 : 0;

  return (
    <Card className="h-100 shadow-sm contractor-card">
      <CardBody>
        <div className="d-flex mb-3">
          <div 
            className="flex-shrink-0 me-3 rounded-circle text-center"
            style={{ 
              backgroundColor: '#f0f0f0', 
              width: '48px', 
              height: '48px', 
              lineHeight: '48px',
              fontWeight: 'bold',
              fontSize: '1.5rem'
            }}
          >
            {avatarLetter}
          </div>
          
          <div className="flex-grow-1">
            <h5 className="card-title mb-1">{contractor.businessName}</h5>
            {renderRating()}
          </div>
        </div>

        <div className="mb-3">
          {contractor.email && (
            <div className="d-flex align-items-center mb-1">
              <Mail size={16} className="text-muted me-2" />
              <span className="text-truncate">{contractor.email}</span>
            </div>
          )}
          
          {contractor.phone && (
            <div className="d-flex align-items-center mb-1">
              <Phone size={16} className="text-muted me-2" />
              <span>{contractor.phone}</span>
            </div>
          )}
          
          {contractor.address && (
            <div className="d-flex align-items-start mb-1">
              <MapPin size={16} className="text-muted me-2 mt-1" />
              <span className="text-wrap">{formatAddress(contractor.address)}</span>
            </div>
          )}

          {contractor.website && (
            <div className="d-flex align-items-center">
              <ExternalLink size={16} className="text-muted me-2" />
              <a 
                href={contractor.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-truncate"
              >
                {contractor.website.replace(/^https?:\/\//, '')}
              </a>
            </div>
          )}
        </div>

        {displayCategories.length > 0 && (
          <div className="mb-3">
            <div className="d-flex flex-wrap gap-1">
              {displayCategories.map((category, index) => (
                <Badge key={index} color="secondary" className="py-1 px-2">
                  {category}
                </Badge>
              ))}
              {additionalCategories > 0 && (
                <Badge color="secondary" className="py-1 px-2">
                  +{additionalCategories} more
                </Badge>
              )}
            </div>
          </div>
        )}

        {contractor.generalInfo && (
          <div className="mb-3">
            <p className="text-muted mb-0 contractor-description">
              {contractor.generalInfo.length > 100
                ? `${contractor.generalInfo.substring(0, 100)}...`
                : contractor.generalInfo}
            </p>
          </div>
        )}

        <div className="mt-auto pt-2 d-flex justify-content-between align-items-center">
          <Link 
            to={`/contractor-profile/${contractor.contractorId}`} 
            className="btn btn-sm btn-primary"
          >
            View Profile
          </Link>
          
          <Link 
            to={`/messages?contractor=${contractor.contractorId}`}
            className="btn btn-sm btn-light"
          >
            Contact
          </Link>
        </div>
      </CardBody>
    </Card>
  );
};

export default ContractorCard;